import { useState } from 'react'
import { useStore } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { postCreateAgentV2Conversation } from '@/agent-v2/conversations/fetch/post-create-agent-v2-conversation'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { AgentV2ConversationSurface } from '@/agent-v2/store/schemas'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { openConversationTab } from '@/chat-common/conversation-navigation/util/open-conversation-tab'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { kRouteSegments } from '@/routing/paths'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedChronEvent } from '@/analytics/utils/medchron-analytics'
import { postInitAgentV2ConversationWithMedChronAsset } from '../fetch/post-init-agent-v2-conversation-with-med-chron-asset'

interface UseStartMedChronAssetChatReturn {
  isLoading: boolean
  startChat: () => Promise<void>
}

type UseStartMedChronAssetChatProps = {
  assetId: string
  matterId: string
}

/**
 * Starts an assistant conversation grounded in the matter's living Medical Chronology:
 * creates the conversation, initializes it with the chronology asset, and opens it.
 */
export function useStartMedChronAssetChat(props: UseStartMedChronAssetChatProps): UseStartMedChronAssetChatReturn {
  const { assetId, matterId } = props
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const startChat = async () => {
    if (isLoading) return
    setIsLoading(true)
    trackMedChronEvent(AnalyticsEvent.MedChronAssetChatClicked, { medChronAssetId: assetId, matterId })

    try {
      const lifecycleGeneration = store.getState().agentV2ConversationsState.lifecycleGeneration
      const conversation = await postCreateAgentV2Conversation(matterId, AgentV2ConversationSurface.WEB)
      await postInitAgentV2ConversationWithMedChronAsset(conversation.id, assetId)
      if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return
      dispatch(AgentV2ConversationsActions.upsertConversationFromSummary(conversation))
      const conversationPath = buildConversationPath(matterId, conversation.id, kRouteSegments.agentV2)

      openConversationTab(conversationPath, navigate)
    } catch (error) {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Failed to start chat. Please try again.',
          durationMs: kToastDurationMediumMs,
        })
      )
      console.error('Error creating medical chronology chat:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, startChat }
}
